// react
import { ChangeEvent, Dispatch, SetStateAction, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback, UserRoleWbg } from '../../../@types';
import { userRolesWbg } from '../../../@types/unions';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Box, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// props
export interface FormAddIfrValues {
	currency: string;
	dateEnd: string;
	dateStart: string;
	name: string;
}

export interface FormAddIfrProps {
	hasNavigate?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddIfr = ({
	hasNavigate = true,
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddIfrProps) => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// Get reporting currency
	const currencyReporting =
		project?.currencies?.find((currency) => currency.type === 'REPORTING') || null;

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState<FormAddIfrValues>({
		currency: currencyReporting?.currency || '',
		dateEnd: '',
		dateStart: '',
		name: '',
	});

	// vars
	const idForm = `form-add-ifr`;

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name as string]: value as string,
		});
	};

	const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
		e.preventDefault();

		setIsLoading(true);

		try {
			let status = 'DRAFT';

			if (user && userRolesWbg.includes(user.role as UserRoleWbg)) {
				status = 'TRIAL';
			}

			const fetchPostIfrsReports = await fetch(`${process.env.REACT_APP_API_URL}/ifrs/reports`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					currency: values.currency,
					dateEnd: values.dateEnd,
					dateStart: values.dateStart,
					name: values.name,
					project: project?.id,
					status,
				}),
			});

			const resPostIfrsReports = await fetchPostIfrsReports.json();

			if (resPostIfrsReports.error) {
				throw new Error(resPostIfrsReports.error.message, {
					cause: {
						id: resPostIfrsReports.error.id,
						details: resPostIfrsReports.error.details,
					},
				});
			}

			const ifrNew = resPostIfrsReports.data[0];

			// success
			onSuccess?.();

			if (hasNavigate) {
				// navigate to new report
				navigate(`/project/${project?.idWbg}/ifr/${ifrNew.id}`);
			} else {
				// set banner
				setBanner({
					hasClose: true,
					message: t('alert.ifrCreated'),
					severity: 'success',
				});

				// close
				onClose?.();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause?.id) {
				if (cause.id === 'IFR_PLAN_VERSION_NOT_FINALIZED') {
					message = t('error.ifrPlanNotFinalized');
				} else if (cause.id === 'IFR_INVALID_STATUS') {
					message = t('error.ifrInvalidStatus');
				} else if (cause.id === 'IFR_UNAUTHORIZED_POST') {
					message = t('error.unauthorized');
				}
			}

			setFeedback({
				alert: {
					children: message,
					severity: 'error',
				},
			});
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	return (
		<Box component="form" id="form-add-ifr" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SelectCurrency
						currencies={[...(new Set(project?.currencies?.map((c) => c.currency)) || [])]}
						id={`${idForm}-currency`}
						name="currency"
						onChange={handleChangeValue}
						required={true}
						value={values.currency}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-start-date`}
						inputProps={{
							max: values.dateEnd,
						}}
						label={t('startDate')}
						name="dateStart"
						type="date"
						onChange={handleChangeValue}
						required
						value={values.dateStart}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-end-date`}
						inputProps={{
							min: values.dateStart,
						}}
						label={t('endDate')}
						name="dateEnd"
						type="date"
						onChange={handleChangeValue}
						required
						value={values.dateEnd}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};
